// Intent: agent-mode client card (stage A, read-only) — contact actions +
// the client's policy list, so an agent on vacation can look a client up and
// call back from the phone. The server 403s any client outside this agent's
// book (caretaker rule), so a stale/forged id shows the error state, no data.
// Mock: mocki-stitch/tryb_agenta/karta_klienta (subset — no contact history yet).

import { useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AgentClientDetail, fetchAgentClientApi } from '@/api/mobile-api';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { ScreenHeader } from '@/components/ui/screen-header';
import { colors, radius, spacing } from '@/theme/tokens';
import { formatDate, formatPln } from '@/utils/format';

function ContactAction({
  icon,
  label,
  onPress,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => ({
        flex: 1,
        backgroundColor: colors.navy,
        borderRadius: radius.button,
        paddingVertical: spacing.sm,
        alignItems: 'center',
        gap: 4,
        transform: [{ scale: pressed ? 0.98 : 1 }],
      })}
    >
      <Ionicons name={icon} size={20} color="#FFFFFF" />
      <AppText variant="labelMd" color="#FFFFFF">
        {label}
      </AppText>
    </Pressable>
  );
}

export default function AgentClientScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [client, setClient] = useState<AgentClientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const customerId = Number(id);
    if (!Number.isFinite(customerId)) {
      setError(t('agentMode.client.loadError'));
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await fetchAgentClientApi(customerId);
      if (cancelled) return;
      if (res.ok && res.data) {
        setClient(res.data);
      } else {
        setError(res.message ?? t('agentMode.client.loadError'));
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  const initials = (client?.name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('agentMode.client.title')} />

        {loaded && error ? (
          <Card style={{ backgroundColor: colors.warningBg }}>
            <AppText variant="bodyLg" color={colors.warning}>
              {error}
            </AppText>
          </Card>
        ) : null}

        {client ? (
          <>
            {/* Identity block: big avatar, name, contact data, has-app badge */}
            <View style={{ alignItems: 'center', gap: spacing.xs }}>
              <View
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: radius.full,
                  backgroundColor: colors.primary,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AppText variant="headlineLg" color="#FFFFFF">
                  {initials}
                </AppText>
              </View>
              <AppText variant="headlineMd">{client.name}</AppText>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, justifyContent: 'center' }}>
                {client.phone ? (
                  <AppText variant="bodyMd">
                    <Ionicons name="call-outline" size={14} color={colors.textSecondary} /> {client.phone}
                  </AppText>
                ) : null}
                {client.email ? (
                  <AppText variant="bodyMd">
                    <Ionicons name="mail-outline" size={14} color={colors.textSecondary} /> {client.email}
                  </AppText>
                ) : null}
              </View>
              {client.hasApp ? (
                <View
                  style={{
                    backgroundColor: colors.infoBg,
                    borderRadius: radius.full,
                    paddingHorizontal: spacing.sm,
                    paddingVertical: 2,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <Ionicons name="shield-checkmark-outline" size={14} color={colors.primary} />
                  <AppText variant="labelMd" color={colors.primary}>
                    {t('agentMode.hasAppBadge')}
                  </AppText>
                </View>
              ) : null}
            </View>

            {/* Contact actions — call and SMS deep links, same as the client's agent card */}
            {client.phone ? (
              <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                <ContactAction
                  icon="call-outline"
                  label={t('agentMode.client.call')}
                  onPress={() => Linking.openURL(`tel:${client.phone}`)}
                />
                <ContactAction
                  icon="chatbox-outline"
                  label={t('agentMode.client.sms')}
                  onPress={() => Linking.openURL(`sms:${client.phone}`)}
                />
                {client.email ? (
                  <ContactAction
                    icon="mail-outline"
                    label={t('agentMode.client.email')}
                    onPress={() => Linking.openURL(`mailto:${client.email}`)}
                  />
                ) : null}
              </View>
            ) : null}

            {/* Read-only policy list */}
            <AppText variant="headlineSm">{t('agentMode.client.policiesHeader')}</AppText>
            {client.policies.length === 0 ? (
              <Card>
                <AppText variant="bodyLg" color={colors.textSecondary}>
                  {t('agentMode.client.noPolicies')}
                </AppText>
              </Card>
            ) : (
              client.policies.map((policy) => (
                <Card key={policy.id} style={{ gap: spacing.xs }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                    <Ionicons name="shield-checkmark" size={20} color={colors.primary} />
                    <AppText variant="headlineSm" style={{ flex: 1 }}>
                      {policy.productName ?? policy.insurerName ?? '—'}
                    </AppText>
                    {policy.premiumGr != null ? (
                      <AppText variant="labelLg" color={colors.navy}>
                        {`${formatPln(policy.premiumGr)} ${
                          policy.premiumPeriod
                            ? t(`premiumPeriod.${policy.premiumPeriod}`, { defaultValue: '' })
                            : ''
                        }`.trim()}
                      </AppText>
                    ) : null}
                  </View>
                  <AppText variant="bodyMd">
                    {[policy.insurerName, policy.policyNumber].filter(Boolean).join(' · ')}
                  </AppText>
                  {policy.endDate ? (
                    <AppText variant="bodyMd">
                      {t('agentMode.client.endsIn', { date: formatDate(policy.endDate) })}
                    </AppText>
                  ) : null}
                </Card>
              ))
            )}
          </>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}
